import React, { Component } from "react"
import styled, { css } from "styled-components"
import Img from "gatsby-image/withIEPolyfill"
import classNames from "classnames"
import { TweenLite, TimelineLite, Power4 } from "gsap/all"
import Swipe from "react-easy-swipe";
import Button from "components/shared/Button"
import prevImg from "images/icons/icon-arrow-prev.svg"
import nextImg from "images/icons/icon-arrow-next.svg"

const Slider = styled.div`
    width: 100%;
    display: block;
    position: relative;
`

const Slide = styled.div`
    position: absolute;
    top: 0;
    height: 100%;
    width: 0%;
    z-index: 10;
    overflow: hidden;

    .slideInner {
        position: absolute;
        height: 100%;
    }
`

const SlideNav = styled.button`
    position: absolute;
    z-index: 22;
    left: 1.5rem;
    bottom: 6rem;
    background-color: transparent;
    border: 0;
    padding: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;

    ${props => props.next && css`
        right: 1.5rem;
        left: auto;
    `}
`

const SlideBlackBg = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 0;
    z-index: 11;
    background-color: rgba(0, 0, 0, .5);
`

const SlideContentContainer = styled.div`
    background: linear-gradient(90deg, rgba(0, 0, 0, .5) 11.31%, rgba(0, 0, 0, 0) 100%);
    position: absolute;
    z-index: 20;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 2rem 2rem 2rem 120px;
    min-height: 40%;
`

const SlideContent = styled.div`
    opacity: 0;
    display: none;

    h3 {
        font-family: ${props => props.theme.font.family.light}
    }
`

const SlideIndicators = styled.ul`
    position: absolute;
    z-index: 21;
    padding: 0;
    margin: 0;
    left: 45px;
    bottom: 2rem;
    list-style: none;

    li {
        margin-bottom: 5px;
        height: 6px;
        width: 6px;
        border: 1px solid ${props => props.theme.colors.white};
        transition: background-color .6s ease;
        background-color: transparent;

        &.active {
            background-color: ${props => props.theme.colors.white};
        }
    }
`

class HeroCarousel extends Component {

    constructor(props) {
        super(props)

        this.state = {
            activeSlide: 0,
            nextSlide: 1,
            prevSlide: 1,
            slideLength: this.props.data.length - 1,
            animating: false,
            swipePosition: 0
        }

        this.initSlider = this.initSlider.bind(this)
        this.addKeyboardNav = this.addKeyboardNav.bind(this)
        this.onSwipe = this.onSwipe.bind(this)
        this.updateSlides = this.updateSlides.bind(this)
        this.setSlideStyles = this.setSlideStyles.bind(this)
        this.changeContent = this.changeContent.bind(this)
        this.slideInBlackBg = this.slideInBlackBg.bind(this)
        this.slideIn = this.slideIn.bind(this)
        this.goToSlide = this.goToSlide.bind(this)
        this.goToNextSlide = this.goToNextSlide.bind(this)
        this.goToPrevSlide = this.goToPrevSlide.bind(this)
        this.nextSlide = this.nextSlide.bind(this)
        this.prevSlide = this.prevSlide.bind(this)
        
        this.slide = []
        this.slideContent = []
    }

    componentDidMount() {
        this.initSlider()
        window.addEventListener('resize', this.initSlider)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.initSlider)
    }

    initSlider() {
        const { activeSlide } = this.state
        const nextSlide = this.nextSlide(activeSlide)
        const prevSlide = this.prevSlide(activeSlide)

        this.setState({
            nextSlide,
            prevSlide
        })

        /** CALC + SET SLIDER SIZE */

        // Reset any width and heights already set
        this.slider.style.height = "auto"
        this.slider.style.width = "auto"

        let slidesInner = this.slider.getElementsByClassName('slideInner')
        for (let i = 0; slidesInner.length > i; i++) {
            slidesInner[i].style.width = `auto`
        }

        // Set active slide relative so we can grab height / width
        const activeSlideEl = this.slide[this.state.activeSlide]
        const slideInner = activeSlideEl.getElementsByClassName('slideInner')
        activeSlideEl.style.position = "relative"
        activeSlideEl.style.width = "100%"
        slideInner[0].style.position = "relative"
        slideInner[0].style.width = "100%"
        slideInner[0].style.left = "0"

        // Grab dimensions
        const height = activeSlideEl.offsetHeight
        const width = activeSlideEl.offsetWidth
        slideInner[0].style.position = "absolute"
        slideInner[0].style.left = "auto"

        // Set dimensions of slider + inner containers
        this.slider.style.height = `${height}px`
        this.slider.style.width = `${width}px`

        for (let i = 0; slidesInner.length > i;  i++) {
            slidesInner[i].style.width = `${width}px`
        }

        // Set initial styles of slides
        this.slide[activeSlide].style.position = "absolute"
        this.slide[activeSlide].style.zIndex = "9"
        this.setSlideStyles(this.slide[prevSlide], "prev")
        this.setSlideStyles(this.slide[nextSlide], "next")
        
        /** END CALC + SET SLIDER SIZE */

        // Show active slide content
        this.slideContent[0].style.opacity = 1
        this.slideContent[0].style.display = "block"

        this.addKeyboardNav()
    }

    addKeyboardNav() {
        let $this = this
        document.onkeydown = (e) => {
            switch (e.keyCode) {
                case 37:
                    $this.goToPrevSlide()
                    break;
                case 39:
                    $this.goToNextSlide()
                    break;
                default:
                    return
            }
        };
    }

    onSwipe(position) {
        Math.sign(position.x) === 1 ? this.goToPrevSlide() : this.goToNextSlide()
    }

    updateSlides() {
        this.slideBlackBg.style.width = "0"
        this.slide[this.state.activeSlide].style.zIndex = "9"

        this.setSlideStyles(this.slide[this.state.prevSlide], "prev")
        this.setSlideStyles(this.slide[this.state.nextSlide], "next")

        this.setState({ animating: false })
    }

    goToSlide(x) {

    }

    goToNextSlide() {
        if (!this.state.animating) {
            // Set / update slide states
            const activeSlide = this.nextSlide()
            const nextSlide = this.nextSlide(activeSlide)
            const prevSlide = this.prevSlide(activeSlide)
            this.setState({
                activeSlide,
                nextSlide,
                prevSlide,
                animating: true
            })

            // Change content
            this.changeContent(this.slideContent[prevSlide], this.slideContent[activeSlide])

            // Animate black container
            this.slideInBlackBg("auto", 0)
            
            // Animate slide in
            this.slideIn(activeSlide)
        }
    }

    goToPrevSlide() {
        if (!this.state.animating) {
            // Set / update slide states
            const activeSlide = this.prevSlide()
            const nextSlide = this.nextSlide(activeSlide)
            const prevSlide = this.prevSlide(activeSlide)
            this.setState({
                activeSlide,
                nextSlide,
                prevSlide,
                animating: true
            })

            // Change content
            this.changeContent(this.slideContent[nextSlide], this.slideContent[activeSlide])

            // Animate black container
            this.slideInBlackBg(0, "auto")

            // Animate slide in
            this.slideIn(activeSlide)
        }
    }

    setSlideStyles(slide, direction) {
        // Refactoring
        const innerSlide = slide.getElementsByClassName('slideInner')[0]

        if (direction === "prev") {
            slide.style.right = "auto"
            slide.style.left = "0"
            innerSlide.style.left = 0
            innerSlide.style.right = "auto"
        } else {
            slide.style.right = "0"
            slide.style.left = "auto"
            innerSlide.style.left = "auto"
            innerSlide.style.right = 0
        }

        slide.style.width = "0"
        slide.style.zIndex = "12"
    }

    slideInBlackBg(left, right) {
        // Refactoring
        const slideBlackBg = new TimelineLite()
        slideBlackBg
            .set(this.slideBlackBg, { css: { left: left, right: right } })
            .to(this.slideBlackBg, 1, { css: { width: "100%" }, ease: Power4.easeIn })
    }

    slideIn(activeSlide) {
        // Refactoring
        new TweenLite(this.slide[activeSlide], 1, { css: { width: "100%" }, onComplete: this.updateSlides, ease: Power4.easeIn, delay: .3 });
    }

    changeContent(slide, activeSlide) {
        // refactoring
        new TweenLite(slide, .6, { css: { display: "none", opacity: "0" }, ease: Power4.easeIn, onComplete: () => {
            new TweenLite(activeSlide, .8, { css: { display: "block", opacity: "1" }, ease: Power4.easeIn, delay: .3 });
        }});
    }

    nextSlide(slide = "") {
        // Workout next slide
        const activeSlide = slide === "" ? this.state.activeSlide : slide
        const nextSlide = (activeSlide + 1) > this.state.slideLength ? 0 : activeSlide + 1

        return nextSlide
    }

    prevSlide(slide = "") {
        // Workout prev slide
        let activeSlide = slide === "" ? this.state.activeSlide : slide
        const prevSlide = (activeSlide - 1) < 0 ? this.state.slideLength : activeSlide - 1
        return prevSlide
    }

    render() {
        const slides = this.props.data.map((item, i) => {
            return(
                <Slide 
                    key={i}
                    ref={slide => this.slide[i] = slide}
                >
                    <div className="slideInner">
                        <Img fluid={item.image_landscape.localFile.childImageSharp.fluid} alt={item.image_landscape.alt_text} className="d-none d-md-block" />
                        <Img fluid={item.image_portrait.localFile.childImageSharp.fluid} alt={item.image_portrait.alt_text} className="d-md-none" />
                    </div>
                </Slide>
            )
        })

        const slideContent = this.props.data.map((item, i) => {
            return(
                <SlideContent 
                    key={i}
                    ref={slideContent => this.slideContent[i] = slideContent}
                >
                    <h2>{item.title}</h2>
                    <h3>{item.sub_title}</h3>
                    <p>{item.description}</p>
                    {item.link_1 !== null &&
                        <Button url={item.link_1.url} target={item.link_1.target}>
                            {item.link_1.title}
                        </Button>
                    }
                    {item.link_2 !== null &&
                        <Button url={item.link_2.url} target={item.link_2.target}>
                            {item.link_2.title}
                        </Button>
                    }
                </SlideContent>
            )
        })

        const slideIndicators = this.props.data.map((item, i) => {
            return(
                <li 
                    onClick={this.goToSlide(i)} 
                    key={i}
                    className={classNames({ active: this.state.activeSlide === i })}
                >
                    <span className="sr-only">Go to slide {i}</span>
                </li>
            )
        })

        return(
            <Slider ref={slider => this.slider = slider}>
                <Swipe onSwipeMove={this.onSwipe}>
                    {slides}
                    <SlideContentContainer>
                        {slideContent}
                    </SlideContentContainer>
                </Swipe>
                <SlideBlackBg ref={slideBlackBg => this.slideBlackBg = slideBlackBg} />
                <SlideIndicators>
                    {slideIndicators}
                </SlideIndicators>
                <SlideNav prev onClick={this.goToPrevSlide}>
                    <img src={prevImg} alt="Prev" width="20"/>
                    <span className="sr-only">Prev</span>
                </SlideNav>
                <SlideNav next onClick={this.goToNextSlide}>
                    <img src={nextImg} alt="Next" width="20"/>
                    <span className="sr-only">Next</span>
                </SlideNav>
            </Slider>
        )
    }
}

export default HeroCarousel